import { apiRequest } from "./api";

export type DeductionRuleType = "PERCENTAGE" | "FIXED";
export type DeductionFrequency = "PER_PERIOD" | "MONTHLY";

export interface DeductionRule {
  type: DeductionRuleType;
  value: number;
}

export interface DynamicDeduction {
  id: string;
  name: string;
  type: DeductionRuleType;
  value: number;
  employerValue?: number;
  frequency?: DeductionFrequency;
  enabled: boolean;
}

export interface DynamicAllowance {
  id: string;
  name: string;
  amount: number;
  enabled: boolean;
}

export interface TaxBracket {
  id: string;
  minIncome: number;
  maxIncome?: number;
  rate: number;
}

export interface TaxConfig {
  enabled: boolean;
  label: string;
  brackets: TaxBracket[];
}

export interface PayrollConfig {
  govDeductions: DynamicDeduction[];
  allowances: DynamicAllowance[];
  storeCommissions: Record<string, number>;
  taxConfig?: TaxConfig;
  /** Fixed working days per month for absence daily-rate calc. Empty = schedule-based. */
  workingDaysPerMonth?: number;
  /** Overtime multiplier (default 1.25) */
  overtimeMultiplier?: number;
  /** Regular holiday work multiplier (default 2.0) */
  regularHolidayMultiplier?: number;
  /** Special non-working day multiplier (default 1.3) */
  specialHolidayMultiplier?: number;
  /** Additional OT multiplier on holiday overtime (default 0.30) */
  holidayOTMultiplier?: number;
  updatedAt: string;
  updatedBy: string;
}

export type SavePayrollConfigInput = Omit<PayrollConfig, "updatedAt" | "updatedBy">;

export type PayrollRunStatus = "PROCESSING" | "COMPLETED" | "APPROVED" | "FAILED";

export interface PayrollRun {
  period: string;
  status: PayrollRunStatus;
  triggeredBy: string;
  totalEmployees: number;
  processedCount: number;
  failedCount: number;
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
  approvedBy?: string;
  approvedAt?: string;
  // Financial summary — persisted at processing completion, updated at approval
  totalGrossPay?: number;
  totalNetPay?: number;
  totalAllowances?: number;
  totalLoanDeductions?: number;
  totalEmployerContributions?: number;
  totalTaxWithheld?: number;
  proratedCount?: number;
  missingEmployees?: Array<{ userId: string; name?: string }>;
}

export const payrollApi = {
  listRuns: () =>
    apiRequest<{ runs: PayrollRun[] }>("/admin/payroll/runs"),

  getConfig: () =>
    apiRequest<{ config: PayrollConfig | null }>("/admin/payroll/config"),

  saveConfig: (input: SavePayrollConfigInput) =>
    apiRequest<{ config: PayrollConfig }>("/admin/payroll/config", {
      method: "PUT",
      body: input,
    }),

  getRunStatus: (period: string) =>
    apiRequest<{ run: PayrollRun | null }>(
      `/admin/payroll/${encodeURIComponent(period)}/status`,
    ),

  process: (period: string) =>
    apiRequest<{ run: PayrollRun }>(
      `/admin/payroll/${encodeURIComponent(period)}/process`,
      { method: "POST" },
    ),

  approve: (period: string) =>
    apiRequest<{ finalized: number }>(
      `/admin/payroll/${encodeURIComponent(period)}/approve`,
      { method: "POST" },
    ),
};

/** Business-scoped payroll API — used by business admins via /biz/{businessId}/payroll/... */
export const bizPayrollApi = {
  listRuns: (businessId: string) =>
    apiRequest<{ runs: PayrollRun[] }>(
      `/biz/${encodeURIComponent(businessId)}/payroll/runs`,
    ),

  getConfig: (businessId: string) =>
    apiRequest<{ config: PayrollConfig | null }>(
      `/biz/${encodeURIComponent(businessId)}/payroll/config`,
    ),

  saveConfig: (businessId: string, input: SavePayrollConfigInput) =>
    apiRequest<{ config: PayrollConfig }>(
      `/biz/${encodeURIComponent(businessId)}/payroll/config`,
      { method: "PUT", body: input },
    ),

  getRunStatus: (businessId: string, period: string) =>
    apiRequest<{ run: PayrollRun | null }>(
      `/biz/${encodeURIComponent(businessId)}/payroll/${encodeURIComponent(period)}/status`,
    ),

  process: (businessId: string, period: string) =>
    apiRequest<{ run: PayrollRun }>(
      `/biz/${encodeURIComponent(businessId)}/payroll/${encodeURIComponent(period)}/process`,
      { method: "POST" },
    ),

  approve: (businessId: string, period: string) =>
    apiRequest<{ finalized: number }>(
      `/biz/${encodeURIComponent(businessId)}/payroll/${encodeURIComponent(period)}/approve`,
      { method: "POST" },
    ),
};
